// Fee formatting + estimation utilities. Kept UI-agnostic so pages, cards,
// search results, and admin previews all use the exact same rules.

const fmt = (n) => Number(n).toLocaleString("id-ID");
const fmtMillion = (n) => {
  // Return "1,5" for 1_500_000 etc. Uses id-ID decimal comma.
  const m = Number(n) / 1_000_000;
  const rounded = Math.round(m * 10) / 10;
  return rounded.toLocaleString("id-ID", { minimumFractionDigits: rounded % 1 === 0 ? 0 : 1, maximumFractionDigits: 1 });
};

/**
 * Format an SPP range per product spec:
 *   • both, min !== max  → "Rp {min}–{max} juta / bulan"
 *   • both, min === max  → "Rp {n} / bulan"
 *   • only min           → "Mulai Rp {n} / bulan"
 *   • empty              → "Informasi biaya belum tersedia"  (NEVER estimate)
 */
export function formatSppRange(spp_min, spp_max, { style = "million" } = {}) {
  const hasMin = spp_min != null && spp_min !== "";
  const hasMax = spp_max != null && spp_max !== "";
  if (hasMin && hasMax) {
    if (Number(spp_min) === Number(spp_max)) {
      const label = style === "million" ? `Rp ${fmtMillion(spp_min)} juta` : `Rp ${fmt(spp_min)}`;
      return `${label} / bulan`;
    }
    if (style === "million") return `Rp ${fmtMillion(spp_min)}–${fmtMillion(spp_max)} juta / bulan`;
    return `Rp ${fmt(spp_min)}–${fmt(spp_max)} / bulan`;
  }
  if (hasMin) {
    const label = style === "million" ? `Rp ${fmtMillion(spp_min)} juta` : `Rp ${fmt(spp_min)}`;
    return `Mulai ${label} / bulan`;
  }
  return "Informasi biaya belum tersedia";
}

/** Format an entry-fee (uang pangkal) range using the same 3-branch rule. */
export function formatEntryRange(entry_fee_min, entry_fee_max) {
  const hasMin = entry_fee_min != null && entry_fee_min !== "";
  const hasMax = entry_fee_max != null && entry_fee_max !== "";
  if (hasMin && hasMax) {
    if (Number(entry_fee_min) === Number(entry_fee_max)) return `Rp ${fmtMillion(entry_fee_min)} juta`;
    return `Rp ${fmtMillion(entry_fee_min)}–${fmtMillion(entry_fee_max)} juta`;
  }
  if (hasMin) return `Mulai Rp ${fmtMillion(entry_fee_min)} juta`;
  return null;
}

/**
 * Compute first-year total using the product spec formula:
 *   Total = entry_fee + (spp_monthly * 12) + annual_fees
 * Returns { min, max, hasEstimate } — hasEstimate=false when spp_min missing
 * (we never guess; UI should hide the estimation card).
 */
export function computeFirstYear(school) {
  if (!school) return { min: null, max: null, hasEstimate: false };
  const sMin = Number(school.spp_min);
  const sMax = Number(school.spp_max);
  const eMin = Number(school.entry_fee_min);
  const eMax = Number(school.entry_fee_max);
  const annual = Number(school.other_annual_fees) || 0;
  if (!Number.isFinite(sMin) || sMin <= 0) return { min: null, max: null, hasEstimate: false };
  const baseEntryLo = Number.isFinite(eMin) && eMin > 0 ? eMin : 0;
  const total_min = baseEntryLo + sMin * 12 + annual;
  const baseEntryHi = Number.isFinite(eMax) && eMax > 0 ? eMax : baseEntryLo;
  const total_max = (Number.isFinite(sMax) && sMax > 0 ? sMax : sMin) * 12 + baseEntryHi + annual;
  return { min: Math.round(total_min), max: Math.round(total_max), hasEstimate: true };
}

/** Format first-year total inline: "Rp 80–100 juta" or "Rp 80 juta" or null when no data. */
export function formatFirstYear(school) {
  const { min, max, hasEstimate } = computeFirstYear(school);
  if (!hasEstimate) return null;
  if (min === max) return `Rp ${fmtMillion(min)} juta`;
  return `Rp ${fmtMillion(min)}–${fmtMillion(max)} juta`;
}

/** Bucket options for the "Anggaran SPP per bulan" filter. */
export const SPP_BUDGET_BUCKETS = [
  { value: "0-2", label: "< Rp 2 juta", max: 2_000_000 },
  { value: "2-5", label: "Rp 2–5 juta", max: 5_000_000 },
  { value: "5-10", label: "Rp 5–10 juta", max: 10_000_000 },
  { value: "10-20", label: "Rp 10–20 juta", max: 20_000_000 },
  { value: "20+", label: "> Rp 20 juta", max: 999_999_999 },
];
